import React from 'react';
import Icon from '../../../components/AppIcon';

const WhatWeDoSection = () => {
  const patientFeatures = [
    {
      icon: 'Activity',
      title: 'Intuitive Symptom Tracking',
      description: 'Log symptoms with smart suggestions and pattern recognition'
    },
    {
      icon: 'Brain',
      title: 'Cognitive Games',
      description: 'Engaging assessments that monitor cognitive health over time'
    },
    {
      icon: 'Watch',
      title: 'Wearable Device Integration',
      description: 'Seamlessly connect fitness trackers and health monitors'
    }
  ];

  const providerFeatures = [
    {
      icon: 'BarChart3',
      title: 'Real-time Patient Data',
      description: 'Access comprehensive patient insights in real-time'
    },
    {
      icon: 'Sparkles',
      title: 'AI-Powered Summaries',
      description: 'Get intelligent analysis and trend identification'
    },
    {
      icon: 'Target',
      title: 'Actionable Insights',
      description: 'Receive clear recommendations for patient care decisions'
    }
  ];

  const FeatureCard = ({ feature, category }) => (
    <div className="group bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:border-primary/20">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
        category === 'patient' ? 'bg-primary/10' : 'bg-accent/10'
      }`}>
        <Icon 
          name={feature?.icon} 
          size={24} 
          className={category === 'patient' ? 'text-primary' : 'text-accent'}
        />
      </div>
      <h3 className="text-lg font-semibold text-card-foreground mb-2 group-hover:text-primary transition-colors">
        {feature?.title}
      </h3>
      <p className="text-muted-foreground leading-relaxed">
        {feature?.description}
      </p>
    </div>
  );

  return (
    <section id="what-we-do" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              What We Do
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              MycoMind addresses the critical gap in neurological healthcare by transforming how patients 
              track their health and how providers understand their patients' daily experiences.
            </p>
          </div>

          {/* Overview */}
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 mb-16">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                Transforming Healthcare Through AI
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We bridge the communication gap between neurological patients and their healthcare providers 
                by capturing, analyzing, and presenting health data in meaningful ways that lead to better 
                care decisions and improved patient outcomes.
              </p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* For Patients */}
            <div>
              <div className="flex items-center mb-8">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">For Patients</h3>
              </div>
              
              <div className="space-y-6">
                {patientFeatures?.map((feature, index) => (
                  <FeatureCard 
                    key={index} 
                    feature={feature} 
                    category="patient"
                  />
                ))}
              </div>
            </div>

            {/* For Providers */}
            <div>
              <div className="flex items-center mb-8">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Stethoscope" size={20} className="text-accent" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">For Healthcare Providers</h3>
              </div>
              
              <div className="space-y-6">
                {providerFeatures?.map((feature, index) => (
                  <FeatureCard 
                    key={index} 
                    feature={feature} 
                    category="provider"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;